import {type PointCloudPosition} from './types';

type Vec3 = PointCloudPosition;

function subtract(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function distance(a: Vec3, b: Vec3): number {
  const d = subtract(a, b);
  return Math.sqrt(dot(d, d));
}

const epsilon = 0.003;

function nearZero(v: number): boolean {
  return Math.abs(v) < epsilon;
}

function areOrthogonal(base: Vec3, p1: Vec3, p2: Vec3, p3: Vec3): boolean {
  const line1 = subtract(base, p1);
  const line2 = subtract(base, p2);
  const line3 = subtract(base, p3);
  return (
    nearZero(dot(line1, line2)) &&
    nearZero(dot(line1, line3)) &&
    nearZero(dot(line2, line3))
  );
}

function combinations<T>(a: T[], min: number, max: number): T[][] {
  min = min || 1;
  max = max < a.length ? max : a.length;
  const fn = (n: number, src: T[], got: T[], allCur: T[][]) => {
    if (n === 0) {
      if (got.length > 0) {
        allCur[allCur.length] = got;
      }
      return;
    }
    for (let j = 0; j < src.length; j++) {
      fn(n - 1, src.slice(j + 1), got.concat([src[j]]), all);
    }
    return;
  };
  const all: T[][] = [];
  for (let i = min; i < a.length; i++) {
    fn(i, a, [], all);
  }
  if (a.length === max) {
    all.push(a);
  }
  return all;
}

/**
 * Turn eight box corners into twelve edges. Pure XYZ math — no Babylon.
 */
export function cornersToEdges(
  corners: Vec3[]
): Array<[PointCloudPosition, PointCloudPosition]> {
  const remainingCorners = new Set(corners);
  const edges: Array<[Vec3, Vec3]> = [];
  const firstCorner = corners[0];
  const firstEnds: Vec3[] = [];

  for (const points of combinations(corners.slice(1), 3, 3)) {
    if (areOrthogonal(firstCorner, points[0], points[1], points[2])) {
      edges.push([firstCorner, points[0]]);
      edges.push([firstCorner, points[1]]);
      edges.push([firstCorner, points[2]]);

      firstEnds.push(points[0], points[1], points[2]);

      remainingCorners.delete(firstCorner);
      remainingCorners.delete(points[0]);
      remainingCorners.delete(points[1]);
      remainingCorners.delete(points[2]);
      break;
    }
  }

  if (firstEnds.length !== 3) {
    throw new Error('Provided corners do not form a box');
  }

  const remainingCornerArray = Array.from(remainingCorners);
  const cattyCorner = [...remainingCornerArray].sort(
    (a, b) => distance(firstCorner, b) - distance(firstCorner, a)
  )[0];

  remainingCorners.delete(cattyCorner);

  const midCorners = Array.from(remainingCorners);
  edges.push([cattyCorner, midCorners[0]]);
  edges.push([cattyCorner, midCorners[1]]);
  edges.push([cattyCorner, midCorners[2]]);

  for (const end of firstEnds) {
    if (areOrthogonal(end, firstCorner, midCorners[0], midCorners[1])) {
      edges.push([end, midCorners[0]]);
      edges.push([end, midCorners[1]]);
    } else if (areOrthogonal(end, firstCorner, midCorners[1], midCorners[2])) {
      edges.push([end, midCorners[1]]);
      edges.push([end, midCorners[2]]);
    } else if (areOrthogonal(end, firstCorner, midCorners[0], midCorners[2])) {
      edges.push([end, midCorners[0]]);
      edges.push([end, midCorners[2]]);
    } else {
      throw new Error('Provided corners do not form a box');
    }
  }

  return edges;
}
